/**
 * @file 视频文件上传
 */

// import sdk, {BosClient} from 'baidubce.sdk';
import {message} from 'antd';
import async from 'async';
import md5 from 'md5';
import {getSTSConfig} from '../services';
import {customCaptureException} from './weirwood';

const PART_SIZE = 100 * 1024 * 1024; // 指定分块大小
const MAX_RETRY_TIMES = 3; // 上传失败重试次数
const DURATION_SECONDS = 43200 * 1000; // 默认过期时间 12h

let client;
let stsConfig;

let lastInit = 0; // 上次注册时间
let retryTimes = 0; // 失败重试次数

export const initBosClient = async (forceUpdate = false) => {
    const now = new Date().getTime();
    if (client && (now - lastInit) < DURATION_SECONDS && !forceUpdate) {
        return;
    }

    stsConfig = (await getSTSConfig() || {}).data;

    const {ak, sk, bucket, endpoint, endpoint_cdn, path, sessionToken} = stsConfig;

    lastInit = new Date().getTime();

    if (!window.baidubce?.sdk) {
        setTimeout(initBosClient, 50);
        return;
    }

    client = new window.baidubce.sdk.BosClient({
        endpoint: `${location.protocol}//${endpoint}`, // 传入Bucket所在区域域名
        bucket,
        sessionToken,
        credentials: {
            ak,
            sk
        }
    });
};

const getTasks = (file, uploadId, bucketName, key) => {
    let leftSize = file.size;
    let offset = 0;
    let partNumber = 1;

    let tasks = [];

    while (leftSize > 0) {
        let partSize = Math.min(leftSize, PART_SIZE);
        tasks.push({
            file,
            uploadId,
            bucketName,
            key,
            partNumber,
            partSize,
            start: offset,
            stop: offset + partSize - 1
        });

        leftSize -= partSize;
        offset += partSize;
        partNumber += 1;
    }
    return tasks;
};

const uploadPartFile = (state, client) => {
    return function (task, callback) {
        let blob = task.file.slice(task.start, task.stop + 1);
        client.uploadPartFromBlob(task.bucketName, task.key, task.uploadId, task.partNumber, task.partSize, blob)
            .then(res => {
                ++state.loaded;
                callback(null, res);
            })
            .catch(err => {
                customCaptureException('UploadBosError-uploadPartFile', {
                    page: '/#/editor',
                    message: task.key,
                    context: 'uploadPartFile',
                    errStr: err.toString(),
                    errJSON: typeof err === 'string' ? err : JSON.stringify(err || {})
                });
                callback(err);
            });
    };
};

const uploadmulti = (file, useMd5, useSuffix) => {
    let uploadId;
    const bucket = stsConfig.bucket;
    const suffix = useSuffix || ((file.type || '').split('/') || [])[1] || 'mp4';
    // const tempArr = file.name.split('.');
    // const suffix = tempArr[tempArr.length - 1];

    const key = useMd5
        ? `${stsConfig.path + md5(`${String(Date.now()) + file.name}`)}.${suffix}`
        : `${stsConfig.path}/${md5(String(Date.now()) + file.name)}.${suffix}`;

    return client.initiateMultipartUpload(bucket, key, {
        config: {
            credentials: {
                ak: stsConfig.ak,
                sk: stsConfig.sk
            },
            sessionToken: stsConfig.sessionToken
        }
    })
        .then(response => {
            uploadId = response.body.uploadId; // 开始上传，获取服务器生成的uploadId
            if (!window.baidubce?.sdk) {
                setTimeout(initBosClient, 50);
                customCaptureException('UploadBosError-noBosSdk', {
                    page: '/#/editor',
                    message: key,
                    context: 'noBosSdk'
                });
                return Promise.reject('window.baidubce.sdk not found');
            }
            let deferred = window.baidubce?.sdk.Q.defer();
            let tasks = getTasks(file, uploadId, bucket, key);
            let state = {
                lengthComputable: true,
                loaded: 0,
                total: tasks.length
            };

            // 为了管理分块上传，使用了async（https://github.com/caolan/async）库来进行异步处理
            let THREADS = 2; // 同时上传的分块数量
            async.mapLimit(tasks, THREADS, uploadPartFile(state, client), (err, results) => {
                if (err) {
                    customCaptureException('UploadBosError-async.mapLimit', {
                        page: '/#/editor',
                        message: key,
                        context: 'async.mapLimit',
                        errStr: err.toString(),
                        errJSON: typeof err === 'string' ? err : JSON.stringify(err || {})
                    });
                    deferred.reject(err);
                }
                else {
                    deferred.resolve(results);
                }
            });
            return deferred.promise;
        })
        .then(allResponse => {
            let partList = [];
            allResponse.forEach((response, index) => {
                // 生成分块清单
                partList.push({
                    partNumber: index + 1,
                    eTag: response.http_headers.etag
                });
            });
            return client.completeMultipartUpload(bucket, key, uploadId, partList); // 完成上传
        })
        .then(res => {
            // 上传完成
            const {endpoint_cdn, bucket} = stsConfig;

            return Promise.resolve({
                url: `${window.location.protocol}//${bucket}.${endpoint_cdn}/${(res.body || {}).key}`
            });
        })
        .catch(err => {
            // 上传失败，添加您的代码
            console.error('upload err:', err);
            try {
                customCaptureException('UploadBosError', {
                    page: '/#/editor',
                    message: key,
                    context: `上次注册时间:${lastInit}, 重复执行次数:${retryTimes}`,
                    errStr: err.toString(),
                    errJSON: typeof err === 'string' ? err : JSON.stringify(err || {})
                });
                retryTimes++;
                if (retryTimes < MAX_RETRY_TIMES) {
                    return uploadMulti(file, useMd5, suffix, true);
                }
            }
            catch (e) {
                // do nothing
            }
            return Promise.resolve();
        });
};

/**
 *
 * @param {*} blob
 * @param {*} bucket Bucket名称，接口返回
 * @param {*} key Object名称，接口返回
 * @param {*} options
 */
export const uploadMulti = async (file, useMd5, suffix, forceUpdate = false) => {
    await initBosClient(forceUpdate);

    return uploadmulti(file, useMd5, suffix);
};
